import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider.jsx'
import { BrandLockup, BrandMark } from '../components/Brand.jsx'

/**
 * Sign-in page — premium edition.
 *
 * Design intent: this is the very first surface a clinician /
 * patient sees, so it doubles as the brand promise. We replaced the
 * plain two-column rectangle with:
 *
 *   • A flowing curved divider between the teal hero (left) and the
 *     credentials form (right) — built from an inline SVG wave path
 *     that bleeds the white form area into the teal hero, so the eye
 *     reads the page as "one canvas with a soft seam" rather than
 *     "two boxes touching".
 *
 *   • Golden hems at the top and bottom of the page (thin amber
 *     gradient hairlines) and around the brand badge — enough to
 *     register as "premium / certified" but never enough to feel
 *     decorative noise.
 *
 *   • A floating glassy form card with rounded corners + a thin gold
 *     accent line under the Sign In button so the call-to-action
 *     has a small flourish without competing with the brand.
 *
 *   • All decorative elements use `pointer-events-none` so the form
 *     remains the only thing the user can click on.
 */
export default function Login() {
  const { signIn, user, error } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()
  const loc = useLocation()
  const from = loc.state?.from?.pathname || '/'

  // If we're already signed in, bounce out.
  useEffect(() => {
    if (user) nav(from, { replace: true })
  }, [user, from, nav])

  async function onSubmit(e) {
    e.preventDefault()
    setBusy(true)
    try {
      await signIn(email, password)
      nav(from, { replace: true })
    } catch {
      /* error is already exposed via useAuth().error */
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50">
      {/* Top gold hairline — sets the "premium document" tone */}
      <GoldHem />

      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative">
        {/* ---- Left: brand hero ---- */}
        <aside className="hidden lg:flex relative overflow-hidden bg-wantok-radial text-white">
          {/* Decorative orbs — soft glows that give depth to the gradient */}
          <div className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-wantok-400/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-amber-400/10 blur-3xl" />
          {/* Subtle gold filament sweeping diagonally across the hero */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background: 'linear-gradient(120deg, transparent 35%, rgba(251,191,36,0.18) 49%, rgba(251,191,36,0.04) 51%, transparent 65%)',
            }}
          />

          <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
            {/* Brand lockup with a gold ring around the badge */}
            <div className="flex items-center gap-3 select-none">
              <span
                className="inline-flex items-center justify-center rounded-full p-[2px]"
                style={{
                  background:
                    'conic-gradient(from 220deg, #fbbf24, #f59e0b, #fcd34d, #f59e0b, #fbbf24)',
                  boxShadow: '0 4px 18px rgba(251,191,36,0.25)',
                }}
              >
                <BrandMark size={56} className="bg-white rounded-full" />
              </span>
              <span className="leading-[1.05]">
                <span className="block font-display font-bold tracking-[-0.01em] text-[20px] text-white">
                  Wantok Telehealth
                </span>
                <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-200/90">
                  ICER Health
                </span>
              </span>
            </div>

            <div className="max-w-md">
              <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
                Connected care<br/>
                <span className="text-wantok-200">for every wantok.</span>
              </h1>
              {/* Slim gold underline — sits beneath the headline as a
                  premium accent without shouting. */}
              <div className="mt-3 h-[2px] w-24 rounded bg-gradient-to-r from-amber-300 via-amber-400 to-transparent" />

              <p className="mt-5 text-wantok-100/95 leading-relaxed">
                Telehealth purpose-built for Papua New Guinea — clinicians,
                pharmacies, laboratories, radiology centres and ambulances
                connected through one clinical record. Data resides in PNG.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-wantok-100">
                <Feature>Single clinical record, every encounter, every clinician</Feature>
                <Feature>FHIR R4 · ICD-10 · HIPAA-aligned</Feature>
                <Feature>Patients see their own labs, imaging, prescriptions</Feature>
                <Feature>Pharmacies upload their own catalogue & pricing</Feature>
              </ul>
            </div>

            <div className="text-[11px] text-wantok-100/75 flex items-center gap-2">
              <span className="inline-block h-px w-8 bg-amber-300/50" />
              <span>© 2026 ICER Health · Wantok Telehealth · Hosted on AWS Sydney · Built for PNG healthcare</span>
            </div>
          </div>
        </aside>

        {/* ---- Right: form ---- */}
        <main className="relative flex items-center justify-center p-6 sm:p-10">
          {/* Soft radial backdrop on the right side — gives the form
              column a faint warm halo without being distracting. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(900px 500px at 70% 50%, rgba(251,191,36,0.06), transparent 60%)',
            }}
          />

          {/* Floating form card — rounded, soft shadow, a thin gold
              hairline along the top that catches light. */}
          <form
            onSubmit={onSubmit}
            className="relative z-10 w-full max-w-sm bg-white/90 backdrop-blur-sm rounded-2xl shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)] border border-slate-200/70 p-7 space-y-5 overflow-hidden"
          >
            {/* Card top hairline — gradient gold band for richness */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-[2px]"
              style={{
                background:
                  'linear-gradient(90deg, transparent, #fbbf24 35%, #f59e0b 50%, #fbbf24 65%, transparent)',
              }}
            />

            {/* Mobile lockup (above the card on small screens) */}
            <div className="lg:hidden mb-1">
              <BrandLockup variant="dark" linkTo={null} size={48} />
            </div>

            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
                Welcome back
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Sign in to your Wantok Telehealth account.
              </p>
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Username or email</span>
              <input
                autoFocus
                type="text"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. demo-patient or you@example.com"
                className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-wantok-500 focus:ring-2 focus:ring-wantok-500/20 outline-none transition"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-wantok-500 focus:ring-2 focus:ring-wantok-500/20 outline-none transition"
              />
            </label>

            {error && (
              <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm">
                {error}
              </div>
            )}

            {/* Sign-in button with a thin gold edge */}
            <button
              type="submit"
              disabled={busy}
              className="relative w-full bg-gradient-to-b from-wantok-600 to-wantok-800 hover:from-wantok-700 hover:to-wantok-900 active:from-wantok-800 active:to-wantok-900 text-white rounded-lg py-2.5 text-sm font-bold tracking-wide shadow-[0_8px_20px_-8px_rgba(15,118,110,0.55)] transition-colors disabled:opacity-60 overflow-hidden"
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[2px]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(251,191,36,0.9), transparent)',
                }}
              />
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="border-t border-slate-200 pt-4 text-center text-xs text-slate-500 space-y-1">
              <div>
                New patient?{' '}
                <Link to="/register" className="text-wantok-700 hover:text-wantok-800 underline font-semibold">
                  Create an account
                </Link>
              </div>
              <div>
                <Link to="/forgot" className="text-slate-500 hover:text-wantok-800 underline">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 text-center lg:hidden">
              © 2026 ICER Health · AWS Sydney · Built for PNG healthcare
            </p>
          </form>
        </main>

        {/* ---- Curved divider (lg+ only) ----
            Sits absolutely between the two columns. The white wave
            bleeds into the teal hero so the seam reads as "soft fold"
            rather than "hard rectangle". */}
        <CurvedDivider />
      </div>

      {/* Bottom gold hairline */}
      <GoldHem position="bottom" />
    </div>
  )
}

// ---------------------------------------------------------------------------

function GoldHem({ position = 'top' }) {
  return (
    <div
      aria-hidden="true"
      className={
        'absolute inset-x-0 h-[3px] z-30 pointer-events-none ' +
        (position === 'top' ? 'top-0' : 'bottom-0')
      }
      style={{
        background:
          'linear-gradient(90deg, transparent 0%, #fcd34d 18%, #f59e0b 35%, #fbbf24 50%, #f59e0b 65%, #fcd34d 82%, transparent 100%)',
        boxShadow: position === 'top'
          ? '0 4px 14px -8px rgba(245,158,11,0.45)'
          : '0 -4px 14px -8px rgba(245,158,11,0.45)',
      }}
    />
  )
}

/**
 * The flowing seam between the teal hero and the white form column.
 *
 * Two stacked SVG paths (white + a thin gold stroke) sit centred on the
 * boundary. Hidden below `lg:` since the page collapses to a single
 * column on phones / tablets and there's no boundary to decorate.
 */
function CurvedDivider() {
  return (
    <div
      aria-hidden="true"
      className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-40 z-20 pointer-events-none"
    >
      <svg
        viewBox="0 0 200 1000"
        preserveAspectRatio="none"
        className="w-full h-full"
      >
        {/* White wave — bleeds the form column into the teal hero */}
        <path
          d="M 200,0 L 100,0 C 60,180 140,330 80,490 C 20,640 160,780 100,1000 L 200,1000 Z"
          fill="rgb(248 250 252)"
        />
        {/* Gold filament tracing the curve — adds the "premium hem" */}
        <path
          d="M 100,0 C 60,180 140,330 80,490 C 20,640 160,780 100,1000"
          fill="none"
          stroke="url(#hemGradient)"
          strokeWidth="1.2"
          opacity="0.85"
        />
        <defs>
          <linearGradient id="hemGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#fcd34d" stopOpacity="0" />
            <stop offset="20%" stopColor="#f59e0b" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#fbbf24" stopOpacity="1" />
            <stop offset="80%" stopColor="#f59e0b" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#fcd34d" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}

function Feature({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400/20 ring-1 ring-amber-300/40">
        <svg viewBox="0 0 24 24" width="12" height="12" className="text-amber-200">
          <path
            d="M5 12.5l4 4 10-10"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>{children}</span>
    </li>
  )
}
